import {
  AssigneeDetails,
  OrderHistory,
  OrderHistoryStatus,
  VendorDelivery,
} from '@buncha/B2B/hooks/type';
import React, {useEffect, useMemo} from 'react';

import {Animated} from 'react-native';
import {Box} from '@buncha/components/core/view';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import DeliveryStep from '../deliveryStep/DeliveryStep';
import DriverStep from '../driverStep/DriverStep';
import OrderPlaceStep from '../orderPlaceStep/OrderPlaceStep';
import {Run} from '@buncha/AboutShift/types';
import ShoppingStep from '../shoppingStep/ShoppingStep';
import VerticalOrderStepper from '../verticalOrderStepper/VerticalOrderStepper';
import {getCurrentStepByDeliverStatus} from '@buncha/B2B/utils';

interface OrderProgressStatusProps {
  orderStatus?: DeliveryLocationStatus;
  orderHistory: OrderHistory[];
  routeOrderId: string;
  assigneeDetails?: AssigneeDetails;
  externalOrderId?: string;
  run?: Run;
  deliveryImages: string[];
  setScreenTitle?: React.Dispatch<React.SetStateAction<string>>;
  legacyId: number;
  socketLoading: boolean;
  stateIndex: number;
  vendorDelivery: VendorDelivery;
  deliveryNotes?: string;
  driverDetails?: AssigneeDetails;
}

const OrderProgressStatus: React.FC<OrderProgressStatusProps> = props => {
  const {
    orderStatus,
    orderHistory,
    routeOrderId,
    assigneeDetails,
    externalOrderId,
    run,
    deliveryImages,
    setScreenTitle,
    legacyId,
    socketLoading,
    stateIndex,
    vendorDelivery,
    deliveryNotes,
    driverDetails,
  } = props;
  let animations: Animated.CompositeAnimation[] = [];
  const ref = Animated.sequence(animations);
  const isClaimed = orderHistory.find(
    item => item.status === OrderHistoryStatus.shoppingInProgress,
  );
  const inTransit = Boolean(
    orderHistory.find(item => item.status === OrderHistoryStatus.inTransit)?.id,
  );
  const deliveryOrderHistory = orderHistory.find(
    item => item.status === OrderHistoryStatus.delivered,
  );
  const currentStepIndex = getCurrentStepByDeliverStatus(orderStatus);
  const isCancelled = orderStatus === DeliveryLocationStatus.cancelled;

  const steps = useMemo(() => {
    if (isCancelled)
      return [
        {
          isCompleted: false,
          zIndex: 2,
          content: (
            <OrderPlaceStep
              active={false}
              orderId={externalOrderId ?? ''}
              runId={run?.id ?? 0}
              legacyId={legacyId}
              orderHistory={orderHistory.find(
                item => item.status === OrderHistoryStatus.notStarted,
              )}
              isCancelled={isCancelled}
            />
          ),
        },
        {
          isCompleted: false,
          zIndex: 1,
          content: (
            <DeliveryStep
              isActive={false}
              deliveryImages={deliveryImages}
              orderHistory={orderHistory.find(
                item => item.status === OrderHistoryStatus.cancelled,
              )}
              failedOrderHistory={orderHistory[orderHistory.length - 1]}
              deliveryLocationStatus={orderStatus}
              setScreenTitle={setScreenTitle}
            />
          ),
        },
      ];
    return [
      {
        isCompleted: Boolean(currentStepIndex >= 0 || isClaimed),
        zIndex: 4,
        content: (
          <OrderPlaceStep
            active={
              orderStatus === DeliveryLocationStatus.notStarted && !isClaimed
            }
            orderId={externalOrderId ?? ''}
            runId={run?.id ?? 0}
            legacyId={legacyId}
            orderHistory={orderHistory.find(
              item => item.status === OrderHistoryStatus.notStarted,
            )}
          />
        ),
      },
      {
        isCompleted: Boolean(currentStepIndex >= 1 || isClaimed || inTransit),
        zIndex: 3,
        content: (
          <ShoppingStep
            isActive={Boolean(
              (orderStatus === DeliveryLocationStatus.notStarted &&
                isClaimed) ||
                (currentStepIndex === 1 && !inTransit && isClaimed),
            )}
            assigneeDetails={assigneeDetails}
            orderId={routeOrderId}
            orderHistory={isClaimed}
            setScreenTitle={setScreenTitle}
            runId={run?.id ?? 0}
            legacyId={legacyId}
          />
        ),
      },
      {
        isCompleted: currentStepIndex >= 2 || inTransit,
        zIndex: 2,
        content: (
          <DriverStep
            isActive={
              currentStepIndex === 2 || (inTransit && currentStepIndex <= 2)
            }
            assigneeDetails={assigneeDetails}
            externalOrderId={externalOrderId ?? ''}
            run={run}
            orderHistory={
              currentStepIndex >= 2
                ? orderHistory.find(
                    item => item.status === OrderHistoryStatus.onTheWay,
                  )
                : orderHistory.find(
                    item => item.status === OrderHistoryStatus.inTransit,
                  )
            }
            setScreenTitle={setScreenTitle}
            stateIndex={stateIndex}
            vendorDelivery={vendorDelivery}
            deliveryNotes={deliveryNotes}
            driverDetails={driverDetails}
          />
        ),
      },
      {
        isCompleted: currentStepIndex >= 3,
        zIndex: 1,
        content: (
          <DeliveryStep
            isActive={currentStepIndex === 3}
            deliveryImages={deliveryImages}
            orderHistory={deliveryOrderHistory}
            failedOrderHistory={orderHistory[orderHistory.length - 1]}
            deliveryLocationStatus={orderStatus}
            setScreenTitle={setScreenTitle}
          />
        ),
      },
    ];
  }, [
    assigneeDetails,
    currentStepIndex,
    deliveryImages,
    deliveryOrderHistory,
    externalOrderId,
    inTransit,
    isCancelled,
    isClaimed,
    legacyId,
    orderHistory,
    orderStatus,
    routeOrderId,
    run,
    setScreenTitle,
    stateIndex,
    vendorDelivery,
    deliveryNotes,
    driverDetails,
  ]);

  const fillProgressList = steps.map(() => ({
    top: new Animated.Value(0),
    bottom: new Animated.Value(0),
  }));

  useEffect(() => {
    if (socketLoading) {
      ref.reset();
      animations = [];
    } else {
      steps.forEach((step, index) => {
        const {top, bottom} = fillProgressList[index];
        const isNextStepCompleted = steps[index + 1]?.isCompleted;
        if (step.isCompleted) {
          animations.push(
            Animated.timing(top, {
              toValue: 1,
              duration: 400,
              useNativeDriver: false,
            }),
          );
          if (isNextStepCompleted)
            animations.push(
              Animated.timing(bottom, {
                toValue: 1,
                duration: 400,
                useNativeDriver: false,
              }),
            );
        }
      });
      ref.start();
    }
    return () => ref.stop();
  }, [animations, fillProgressList, ref, socketLoading, steps]);

  return (
    <Box>
      {steps.map((step, index) => (
        <VerticalOrderStepper
          key={index}
          index={index}
          step={{...step, isNextCompleted: steps[index + 1]?.isCompleted}}
          length={steps.length}
          fillTop={fillProgressList[index].top}
          fillBottom={fillProgressList[index].bottom}
          deliveryLocationStatus={orderStatus}
        />
      ))}
    </Box>
  );
};

export default React.memo(OrderProgressStatus);
